import React, { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRegisterDropSlot,
  useCollisionSlotId,
  useEditorDragItem,
} from './dnd/drop-slot-registry';
import {
  isDropSlotEnabled,
  isRowDrag,
  shouldHideFillDropZoneDuringDrag,
} from '../domain/dnd/rules';
import { resolveDropIndicatorLabel } from '../domain/dnd/drop-labels';
import type { DragData, DropData } from '../domain/dnd/types';
import { getEditorStoreState } from '../domain/store';

type DropSlotProps = {
  id: string;
  data: DropData;
  variant?: 'block' | 'section' | 'group-row' | 'fill' | 'group-row-fill';
  rowHitAnchor?: 'between' | 'below';
  empty?: boolean;
  subtle?: boolean;
  onEmptyClick?: () => void;
};

type DropZoneState = 'idle' | 'active' | 'rejected';

function resolveFillZone(
  empty: boolean,
  drag: DragData | undefined,
  dragging: boolean,
  over: boolean,
  hideDuringDrag: boolean,
  t: (key: string) => string,
): { state: DropZoneState; label: string } | null {
  if (!empty) {
    return null;
  }

  if (dragging && hideDuringDrag) {
    return null;
  }

  if (dragging && isRowDrag(drag)) {
    return { state: 'rejected', label: t("Can't drop here") };
  }
  if (over) {
    return { state: 'active', label: t('Drop blocks here') };
  }
  return { state: 'idle', label: t('Drop blocks here') };
}

function resolveGroupRowFillZone(
  empty: boolean,
  drag: DragData | undefined,
  dragging: boolean,
  over: boolean,
  accepts: boolean,
  hideDuringDrag: boolean,
  targetLabel: string,
  t: (key: string) => string,
): { state: DropZoneState; label: string } | null {
  if (!empty) {
    return null;
  }

  const idleLabel = t('Drop layout here');

  if (!dragging || !drag) {
    return { state: 'idle', label: idleLabel };
  }

  if (hideDuringDrag) {
    return null;
  }

  if (!accepts) {
    return { state: 'rejected', label: t("Can't drop here") };
  }

  return {
    state: over ? 'active' : 'idle',
    label: targetLabel,
  };
}

export const DropSlot = memo(({
  id,
  data,
  variant = 'block',
  rowHitAnchor = 'between',
  empty = false,
  subtle = false,
  onEmptyClick,
}: DropSlotProps) => {
  const { t } = useTranslation();
  const sections = getEditorStoreState().document.sections;
  const collisionSlotId = useCollisionSlotId();
  const dragItem = useEditorDragItem();
  const hitRef = useRef<HTMLDivElement | null>(null);
  const drag = dragItem ?? undefined;
  const accepts = isDropSlotEnabled(sections, data, drag);
  const dragging = Boolean(dragItem);
  const collisionOver = collisionSlotId === id && accepts;
  const over = collisionOver;
  const hideFillZoneDuringDrag = shouldHideFillDropZoneDuringDrag(
    sections,
    data,
    drag,
  );

  const getRect = useCallback(
    () => hitRef.current?.getBoundingClientRect(),
    [],
  );
  useRegisterDropSlot({ id, data, getRect });

  const setHitRef = useCallback((node: HTMLDivElement | null) => {
    hitRef.current = node;
  }, []);

  const handleEmptyClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (dragging || onEmptyClick === undefined) {
        return;
      }

      event.stopPropagation();
      onEmptyClick();
    },
    [dragging, onEmptyClick],
  );

  if (variant === 'fill' || variant === 'group-row-fill') {
    const zone =
      variant === 'group-row-fill'
        ? resolveGroupRowFillZone(
            empty,
            drag,
            dragging,
            over,
            accepts,
            hideFillZoneDuringDrag,
            resolveDropIndicatorLabel(drag, data, sections, t),
            t,
          )
        : resolveFillZone(empty, drag, dragging, over, hideFillZoneDuringDrag, t);

    const tone = variant === 'group-row-fill' ? 'group' : 'column';

    return (
      <div className={"email-builder-drop-slot-fill-slot"} data-drop-slot="true">
        <div ref={setHitRef} className={"email-builder-drop-slot-fill-hit"} data-tone={tone}>
          {zone ? (
            <div
              className={"email-builder-drop-slot-drop-zone"}
              data-state={zone.state}
              data-subtle={subtle ? 'true' : 'false'}
              data-tone={tone}
              data-clickable={
                variant === 'fill' && !dragging && onEmptyClick !== undefined
                  ? 'true'
                  : 'false'
              }
              onClick={
                variant === 'fill' && onEmptyClick !== undefined
                  ? handleEmptyClick
                  : undefined
              }>
              {zone.label}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const show = Boolean(dragging && accepts);

  const isSectionSlot = variant === 'section' || variant === 'group-row';

  return (
    <div
      className={isSectionSlot ? "email-builder-drop-slot-row-slot" : "email-builder-drop-slot-slot"}
      data-drop-slot="true">
      <div
        ref={setHitRef}
        className={isSectionSlot ? "email-builder-drop-slot-row-hit" : "email-builder-drop-slot-hit"}
        data-row-hit-anchor={isSectionSlot ? rowHitAnchor : undefined}
        data-active={show ? 'true' : 'false'}
        data-over={over ? 'true' : 'false'}
      />
    </div>
  );
});
DropSlot.displayName = 'DropSlot';
